/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingGroupScreening.module.scss';

import { type ScreenResult, type ScreenRow } from './TransferPricingGroupScreening.types';

type Translate = (key: string) => string;

const ROWS: ScreenRow[] = [
  { name: 'Aurex Vertrieb GmbH', ref: 'HRB 41 882 · parent', flag: '🇩🇪', result: 'clear', group: 'entities' },
  { name: 'Aurex Baustoff Handel GmbH', ref: 'HRB 88 214', flag: '🇩🇪', result: 'clear', group: 'entities' },
  { name: 'Aurex Nederland BV', ref: 'KvK 61 204 883', flag: '🇳🇱', result: 'clear', group: 'entities' },
  { name: 'Aurex France SAS', ref: 'SIREN 812 447 909', flag: '🇫🇷', result: 'clear', group: 'entities' },
  { name: 'Aurex Polska Sp. z o.o.', ref: 'KRS 0000418702', flag: '🇵🇱', result: 'clear', group: 'entities' },
  {
    name: 'Aurex Trading DMCC',
    ref: 'DMCC-2214 · free zone',
    flag: '🇦🇪',
    result: 'review',
    reason: 'high-risk jurisdiction',
    group: 'entities',
  },
  {
    name: 'Aurex Vostok OOO',
    ref: 'OGRN 1187746032118',
    flag: '🇷🇺',
    result: 'match',
    reason: 'EU 833/2014 · UK OFSI',
    group: 'entities',
  },
  { name: 'Katharina Voss', ref: 'Geschäftsführer', flag: '🇩🇪', result: 'clear', group: 'officers' },
  { name: 'Henk de Vries', ref: 'Director', flag: '🇳🇱', result: 'clear', group: 'officers' },
  { name: 'Marc Lavelle', ref: 'Director', flag: '🇫🇷', result: 'clear', group: 'officers' },
  {
    name: 'Dmitri Sorokin',
    ref: 'Director · UBO 22%',
    flag: '🇷🇺',
    result: 'review',
    reason: 'PEP — regional office',
    group: 'officers',
  },
  { name: 'Ana Ferreira', ref: 'Company secretary', flag: '🇵🇹', result: 'clear', group: 'officers' },
];

const getBullets = (t: Translate): string[] => [
  t('Ownership is resolved from the register first, so the perimeter is not the one the counterparty drew'),
  t('Branches and dormant entities are screened, not only the trading subsidiaries'),
  t('Indirect holdings are aggregated, so a chain of minority stakes that adds up to control is caught'),
  t(
    'Every hit names its list — OFAC, the EU consolidated list, UK OFSI, the UN list or PEP data — with the date it appeared and the instrument behind it',
  ),
];

const ENTITY_COUNT = ROWS.filter((row) => row.group === 'entities').length;
const OFFICER_COUNT = ROWS.filter((row) => row.group === 'officers').length;
const LISTS_COUNT = 6;
const FLAGGED_COUNT = ROWS.filter((row) => row.result !== 'clear').length;
const CLEAR_COUNT = ROWS.length - FLAGGED_COUNT;
const VERDICT = `${FLAGGED_COUNT} flagged · ${CLEAR_COUNT} clear`;

const RESULT_CLASS: Record<ScreenResult, string> = {
  clear: styles.rClear,
  review: styles.rReview,
  match: styles.rMatch,
};

const LEAD = 900;
const STEP = 330;
const HOLD = 3200;
const LOOP = LEAD + ROWS.length * STEP + HOLD;

export const TransferPricingGroupScreening = () => {
  const { t } = useInt();
  const bullets = getBullets(t);
  const rootRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const barRef = useRef<HTMLDivElement>(null);
  const pipRef = useRef<HTMLSpanElement>(null);
  const statRef = useRef<HTMLSpanElement>(null);
  const replayRef = useRef<HTMLButtonElement>(null);
  const kERef = useRef<HTMLSpanElement>(null);
  const kORef = useRef<HTMLSpanElement>(null);
  const kLRef = useRef<HTMLSpanElement>(null);
  const kHRef = useRef<HTMLSpanElement>(null);
  const kHWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return undefined;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let timers: ReturnType<typeof setTimeout>[] = [];
    let raf: number | null = null;
    let live = true;
    let started = false;

    const at = (ms: number, fn: () => void) => {
      timers.push(setTimeout(fn, ms));
    };

    const clearAll = () => {
      timers.forEach(clearTimeout);
      timers = [];

      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    };

    const count = (el: HTMLSpanElement | null, to: number, dur: number) => {
      let t0: number | null = null;

      const tick = (ts: number) => {
        if (t0 === null) {
          t0 = ts;
        }

        const p = Math.min((ts - t0) / dur, 1);
        const e = 1 - (1 - p) ** 3;

        if (el) {
          el.textContent = String(Math.round(to * e));
        }

        if (p < 1) {
          raf = requestAnimationFrame(tick);
        }
      };

      raf = requestAnimationFrame(tick);
    };

    const reset = () => {
      rowRefs.current.forEach((row) => {
        row?.classList.remove(styles.on, styles.done, styles.rClear, styles.rReview, styles.rMatch);
      });

      if (kERef.current) kERef.current.textContent = '0';
      if (kORef.current) kORef.current.textContent = '0';
      if (kLRef.current) kLRef.current.textContent = '0';
      if (kHRef.current) kHRef.current.textContent = '0';

      kHWrapRef.current?.classList.remove(styles.kpiLive);
      replayRef.current?.classList.remove(styles.show);
      pipRef.current?.classList.add(styles.live);

      if (barRef.current) {
        barRef.current.style.width = '0%';
      }
    };

    const settle = () => {
      rowRefs.current.forEach((row, index) => {
        row?.classList.remove(styles.on);
        row?.classList.add(styles.done, RESULT_CLASS[ROWS[index].result]);
      });

      if (kERef.current) kERef.current.textContent = String(ENTITY_COUNT);
      if (kORef.current) kORef.current.textContent = String(OFFICER_COUNT);
      if (kLRef.current) kLRef.current.textContent = String(LISTS_COUNT);
      if (kHRef.current) kHRef.current.textContent = String(FLAGGED_COUNT);

      kHWrapRef.current?.classList.add(styles.kpiLive);

      if (barRef.current) {
        barRef.current.style.width = '100%';
      }

      pipRef.current?.classList.remove(styles.live);

      if (statRef.current) {
        statRef.current.textContent = VERDICT;
      }

      replayRef.current?.classList.add(styles.show);
    };

    const play = () => {
      if (!live) {
        return;
      }

      clearAll();
      reset();

      if (statRef.current) {
        statRef.current.textContent = 'Resolving group from register';
      }

      count(kERef.current, ENTITY_COUNT, 700);
      at(180, () => count(kORef.current, OFFICER_COUNT, 620));
      at(360, () => count(kLRef.current, LISTS_COUNT, 520));

      let flagged = 0;

      at(LEAD - 120, () => {
        if (statRef.current) {
          statRef.current.textContent = `Screening ${ROWS.length} subjects`;
        }
      });

      ROWS.forEach((row, index) => {
        at(LEAD + index * STEP, () => {
          rowRefs.current.forEach((el) => el?.classList.remove(styles.on));
          rowRefs.current[index]?.classList.add(styles.on);
        });

        at(LEAD + index * STEP + 240, () => {
          const el = rowRefs.current[index];

          el?.classList.remove(styles.on);
          el?.classList.add(styles.done, RESULT_CLASS[row.result]);

          if (row.result !== 'clear') {
            flagged += 1;

            if (kHRef.current) {
              kHRef.current.textContent = String(flagged);
            }

            kHWrapRef.current?.classList.add(styles.kpiLive);
          }
        });
      });

      at(LEAD + ROWS.length * STEP + 120, () => {
        rowRefs.current.forEach((el) => el?.classList.remove(styles.on));
        pipRef.current?.classList.remove(styles.live);

        if (statRef.current) {
          statRef.current.textContent = VERDICT;
        }

        replayRef.current?.classList.add(styles.show);
      });

      const t0 = performance.now();

      const sweep = () => {
        if (!live) {
          return;
        }

        const elapsed = (performance.now() - t0) / LOOP;

        if (barRef.current) {
          barRef.current.style.width = `${Math.min(elapsed, 1) * 100}%`;
        }

        if (elapsed < 1) {
          requestAnimationFrame(sweep);
        }
      };

      sweep();
      at(LOOP, play);
    };

    const replayButton = replayRef.current;

    const onReplayClick = () => {
      live = true;
      play();
    };

    replayButton?.addEventListener('click', onReplayClick);

    if (reduceMotion) {
      settle();

      return () => replayButton?.removeEventListener('click', onReplayClick);
    }

    if (!('IntersectionObserver' in window)) {
      settle();

      return () => replayButton?.removeEventListener('click', onReplayClick);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            play();
          } else if (!entry.isIntersecting) {
            live = false;
            clearAll();
          } else if (entry.isIntersecting && started) {
            live = true;
            play();
          }
        });
      },
      { threshold: 0.22 },
    );

    observer.observe(root);

    return () => {
      live = false;
      clearAll();
      observer.disconnect();
      replayButton?.removeEventListener('click', onReplayClick);
    };
  }, []);

  return (
    <Section className={styles.section}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            align="left"
            className={styles.titleBlock}
            title={t('Screen the entire group and everyone who controls it.')}
            description={t(
              'A check that stops at the top entity misses the subsidiaries, the branches and the people who sign for them. Exposure sits in the parts of a structure nobody looked up.',
            )}
          />

          <ul className={styles.list}>
            {bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>

        <div className={styles.card} ref={rootRef}>
          <div className={styles.bar} ref={barRef} />

          <div className={styles.cardHead}>
            <span className={styles.cardTitle}>Group screen — Aurex Vertrieb GmbH</span>
            <span className={styles.cardMeta}>DE · HRB 41 882</span>
          </div>

          <div className={styles.kpis}>
            <div>
              <span className={styles.kpiKey}>Entities</span>
              <span className={styles.kpiValue} ref={kERef}>
                0
              </span>
            </div>
            <div>
              <span className={styles.kpiKey}>Officers &amp; UBOs</span>
              <span className={styles.kpiValue} ref={kORef}>
                0
              </span>
            </div>
            <div>
              <span className={styles.kpiKey}>Lists</span>
              <span className={styles.kpiValue} ref={kLRef}>
                0
              </span>
            </div>
            <div className={styles.kpiHit} ref={kHWrapRef}>
              <span className={styles.kpiKey}>To review</span>
              <span className={styles.kpiValue} ref={kHRef}>
                0
              </span>
            </div>
          </div>

          <div className={styles.sectionLabel}>Entities in the group</div>

          {ROWS.filter((row) => row.group === 'entities').map((row) => {
            const index = ROWS.indexOf(row);

            return (
              <div
                key={row.name}
                className={styles.row}
                ref={(el) => {
                  rowRefs.current[index] = el;
                }}
              >
                <span className={styles.rowName}>{row.name}</span>
                <span className={styles.rowRef}>{row.ref}</span>
                <span className={styles.rowFlag}>{row.flag}</span>
                <span className={styles.rowStatus}>
                  <span className={styles.rowDot} />
                  <span className={styles.rowWord}>
                    {row.result === 'clear' ? 'Clear' : row.result === 'review' ? 'Review' : 'Match'}
                    {row.reason && <i>{row.reason}</i>}
                  </span>
                </span>
              </div>
            );
          })}

          <div className={styles.sectionLabel}>Officers and beneficial owners</div>

          {ROWS.filter((row) => row.group === 'officers').map((row) => {
            const index = ROWS.indexOf(row);

            return (
              <div
                key={row.name}
                className={styles.row}
                ref={(el) => {
                  rowRefs.current[index] = el;
                }}
              >
                <span className={styles.rowName}>{row.name}</span>
                <span className={styles.rowRef}>{row.ref}</span>
                <span className={styles.rowFlag}>{row.flag}</span>
                <span className={styles.rowStatus}>
                  <span className={styles.rowDot} />
                  <span className={styles.rowWord}>
                    {row.result === 'clear' ? 'Clear' : row.result === 'review' ? 'Review' : 'Match'}
                    {row.reason && <i>{row.reason}</i>}
                  </span>
                </span>
              </div>
            );
          })}

          <div className={styles.foot}>
            <span className={styles.pip} ref={pipRef} />
            <span className={styles.status} ref={statRef} />
            <button type="button" className={styles.replay} ref={replayRef}>
              Replay
            </button>
          </div>
        </div>
      </div>
    </Section>
  );
};
